import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TableEditingActionsTypes } from 'src/app/components/shared/sharedModules/designSystem/models/tableEditingActionsTypes.enum';
import { MuMapComponent } from 'src/app/components/shared/sharedModules/designSystem/mu-map/mu-map.component';
import { TableRowEditor } from 'src/app/components/shared/sharedModules/designSystem/mu-table/mu-table.component';
import { StorageService } from 'src/app/components/shared/sharedServices/storage.service';
import { Actions } from 'src/app/components/shared/types/actions.type';
import { Filters } from 'src/app/components/shared/types/filters.type';
import { Flags } from 'src/app/components/shared/types/flags.type';
import { MuComponent } from 'src/app/components/shared/types/MuComponent.componetent';
import { ProcessingData } from 'src/app/components/shared/types/processingData.type';
import { MU_ROW_INDEX, TableColumn } from 'src/app/components/shared/types/tableColumn.type';
import { environment } from 'src/environments/environment';
import { massiveConstants } from '../../massive.constants';
import { MassiveService } from '../../massive.service';
import { ServiceDataList } from '../../models/serviceDataList.model';
import { Service } from '../../models/services.model';
import { ServicesFilters } from '../../models/servicesFilters.model';
import { MassiveServiceFlags } from './massive-services.flags';
import MarkerClusterer from '@googlemaps/markerclustererplus';
import { TableOption } from '../../models/tableOption.interface';
import { MuLayoutPageAndMapService } from 'src/app/components/shared/sharedProtos/layout/mu-layout-page-and-map.service';

@Component({
  selector: 'app-massive-services',
  templateUrl: './services.component.html',
  styleUrls: ['./services.component.sass'],
})
export class ServicesComponent extends MuComponent {
  /**
   * This flag tells us if the request is already routed
   */
  routed: boolean;
  /**
   * the request id
   */
  requestId: number;
  /**
   * the flags to route the request
   */
  flags = new Flags<MassiveServiceFlags>();
  /**
   * the services of the table
   */
  servicesFilters = new Filters<ServicesFilters>(
    { storage: this.storageService, key: 'massive.services', data: { page: 1, limit: 25 }, delay: 500 },
    'page',
    'limit'
  );
  /**
   * The services data
   */
  servicesDataList: ServiceDataList;
  /**
   * edit table action
   */
  servicesActions = new Actions<TableEditingActionsTypes, TableRowEditor<Service>>();
  /**
   * columns definition of the table
   */
  servicesTableColumns: TableColumn<Service, ServicesFilters>[] = [];
  /**
   * The row options
   */
  tableOptionList: TableOption[] = [];
  /**
   * Loading excel info
   */
  processingRoute: ProcessingData<MassiveServiceFlags>;
  /**
   * Service markers
   */
  markers: { [key: string]: google.maps.Marker } = {};
  /**
   * Selected marker
   */
  selectedMarker: google.maps.Marker;
  /**
   * map Cluster
   */
  mapCluster: MarkerClusterer;
  /**
   * Google map
   */
  gmap: google.maps.Map<Element>;

  constructor(
    private layoutPageAndMapService: MuLayoutPageAndMapService,
    route: ActivatedRoute,
    private storageService: StorageService,
    private massiveService: MassiveService,
    private router: Router
  ) {
    super();
    layoutPageAndMapService.setup({
      columnSize: {
        startColumn: 8,
        endColumn: 4,
      },
      overflowContent: true,
      texts: {
        icon: '',
        title: '',
        subTitle: '',
      },
    });
    this.watch(route.params).subscribe(params => {
      const rId = params.requestId as string;
      this.routed = rId.startsWith('r');
      this.requestId = parseInt(this.routed ? rId.substring(1) : rId, 10);
    });
  }

  muOnInit() {
    this.servicesActions.on(TableEditingActionsTypes.SAVE_ROW_EDITING, async (_, value) => {
      this.servicesDataList.allowRoute = false;
      const rowEditor = value as TableRowEditor<Service>;
      rowEditor.save();
      const rowStatus = await this.massiveService.updateMassiveDeliveryService(
        this.servicesDataList.values[rowEditor.index],
        this.requestId
      );
      this.servicesDataList.allowRoute = rowStatus.allowToRoute;
      const coord = new google.maps.LatLng(Number(rowStatus.lat), Number(rowStatus.lng));
      const marker = this.markers[rowStatus.serviceId];
      marker.setPosition(coord);
      this.mapCluster.removeMarker(marker);
      this.mapCluster.addMarker(marker);
      this.gmap.setCenter(coord);
      setTimeout(() => {
        this.fitMapBounds();
      });
      rowEditor.end();
      this.servicesFilters.emitFilterChanges();
    });
    this.servicesActions.on(TableEditingActionsTypes.CANCEL_ROW_EDITING, (_, value) => {
      const rowEditor = value as TableRowEditor<Service>;
      rowEditor.cancel();
      rowEditor.end();
    });
    this.processingRoute = new ProcessingData<MassiveServiceFlags>({
      flags: this.flags,
      errorFlag: MassiveServiceFlags.ROUTING_FAIL,
      showFlag: MassiveServiceFlags.ROUTING_PACKAGES,
      title: 'context.massive.routePackages.title',
      message: 'context.massive.routePackages.message',
      errorTitle: 'context.massive.routePackages.errorTitle',
    });
    this.tableOptionList = [
      {
        actionType: TableEditingActionsTypes.START_ROW_EDITING,
        icon: 'edit',
      },
    ];
    this.servicesTableColumns = [
      new TableColumn({
        key: MU_ROW_INDEX,
        className: 'mu-masivos-srv-tbl-col-1',
        getValue: this.getRowIndex.bind(this),
      }),
      new TableColumn({
        key: 'idClient',
        className: 'mu-masivos-srv-tbl-col-2',
        editable: true,
      }),
      new TableColumn({
        key: 'clientName',
        className: 'mu-masivos-srv-tbl-col-2',
        editable: true,
      }),
      new TableColumn({
        key: 'address',
        className: 'mu-masivos-srv-tbl-col-3',
        editable: true,
      }),
      new TableColumn({
        key: 'lat',
        className: 'mu-masivos-srv-tbl-col-2',
        editable: true,
        getValue: this.getLatLng.bind(this),
      }),
      new TableColumn({
        key: 'weight',
        className: 'mu-masivos-srv-tbl-col-1',
      }),
      new TableColumn({
        key: 'phone',
        className: 'mu-masivos-srv-tbl-col-2',
      }),
      new TableColumn({
        label: 'options',
        className: 'mu-masivos-srv-tbl-col-2',
      }),
    ];
    this.flags.enable(MassiveServiceFlags.LOAD_SERVICES_TABLE);
    this.watch(this.servicesFilters.filtersChanged).subscribe(filters => {
      this.loadServices(filters);
    });
    this.servicesFilters.emitFilterChanges();
    this.watch(this.layoutPageAndMapService.readyMap).subscribe(map => {
      this.gmap = map.map;
      this.loadServicesMap(map);
    });
    this.watch(this.flags.valueChanges(MassiveServiceFlags.CREATE_SERVICES)).subscribe(value => {
      if (!value.value) {
        return environment.guardClause;
      }
      if (!this.servicesDataList || !this.servicesDataList.allowRoute) {
        return environment.guardClause;
      }
      if (this.routed) {
        this.router.navigate(['masivos/program', this.requestId]);
      }
      this.routeServices();
    });
  }

  /**
   * concat lat and lng
   */
  getLatLng(service: Service) {
    return `${service.lat} / ${service.lng}`;
  }

  getRowIndex(service: Service) {
    const { limit, page } = this.servicesFilters.filterData;
    const realPage = page - 1;
    return limit * realPage + service.muIndex + 1;
  }

  /**
   * call valhalla routing service
   */
  routeServices() {
    this.processingRoute.begin();
    const streaming = this.massiveService.route(this.requestId);
    if (!streaming || typeof streaming.on !== 'function') {
      return environment.guardClause;
    }
    streaming.on('data', data => {
      if (data.getStatus() === 'TICK') {
        return environment.guardClause;
      }
      if (data.getStatus() === 'END') {
        this.processingRoute.end();
        this.router.navigate(['masivos/program', this.requestId]);
        return environment.guardClause;
      }
      if (data.getStatus() !== 'OK') {
        this.flags.disable(MassiveServiceFlags.CREATE_SERVICES);
        const msg = data.getResult();
        if (msg.includes('{')) {
          const { type, ...payload } = JSON.parse(msg);
          this.processingRoute.markError(`context.massive.processingExcel.${type}`, payload);
          return environment.guardClause;
        }
        this.processingRoute.markError(data.getResult());
        return environment.guardClause;
      }
      this.processingRoute.init(data.getRecordsquantity());
      this.processingRoute.setProgress(data.getRecordsprocessed());
    });
    streaming.on('error', err => {
      this.processingRoute.markError('context.massive.routePackages.genericErrorMessage');
    });
  }

  buildMarkerClusterer(gmap): void {
    this.mapCluster = new MarkerClusterer(gmap, [], {
      imagePath: 'https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m',
      maxZoom: massiveConstants.clusterMaxZoom,
    });
  }

  /**
   *
   * @param map get all the points in the map
   */
  loadServicesMap(map: MuMapComponent<any>): void {
    const streaming = this.massiveService.listMassiveDeliveryServiceCoords(this.requestId);
    if (!streaming || typeof streaming.on !== 'function') {
      return environment.guardClause;
    }
    map.removeMarkers();
    const gmap = map.map;
    const bounds = new google.maps.LatLngBounds();
    this.buildMarkerClusterer(gmap);
    let mapCounter = 0;
    streaming.on('data', data => {
      data.getServicesList().forEach((service, index) => {
        const serviceId = service.getServiceid();
        const rowIndex = serviceId !== 'CEDI' ? ++mapCounter : 0;
        setTimeout(() => {
          const position = {
            lat: Number(service.getLat()),
            lng: Number(service.getLng()),
          };
          const zIndex = massiveConstants.normalZIndex;
          let marker: google.maps.Marker = null;
          if (serviceId === 'CEDI') {
            marker = new google.maps.Marker({
              position,
              title: 'Centro de Distribucion',
              label: 'CEDI',
              icon: massiveConstants.selectedIcon,
              map: gmap,
              zIndex: massiveConstants.cediZIndex,
            });
          } else {
            marker = new google.maps.Marker({
              clickable: true,
              position,
              title: `${service.getOrderid()}: ${service.getClientname()}`,
              label: {
                fontSize: '12px',
                fontWeight: 'bold',
                text: `${rowIndex}`,
              },
              icon: massiveConstants.normalIcon,
              map: gmap,
              zIndex,
            });
            marker.addListener('click', () => {
              this.findRow(serviceId, rowIndex);
            });
          }
          this.markers[serviceId] = marker;
          bounds.extend(marker.getPosition());
          gmap.fitBounds(bounds);
          this.mapCluster?.addMarker(marker);
        }, 0);
      });
    });
  }

  /**
   * move the table to the page where the row is located
   */
  findRow(serviceId: string, rowIndex: number) {
    this.selectMapMarker(serviceId);
    const { limit, page } = this.servicesFilters.filterData;
    const nextPage = rowIndex % limit;
    const goToPage = (rowIndex - nextPage) / limit + (nextPage ? 1 : 0);
    if (goToPage !== page) {
      this.servicesFilters.emitFilterValue('page', goToPage);
    }
  }

  /**
   * update map bounds
   */
  fitMapBounds() {
    const bounds = new google.maps.LatLngBounds();
    for (const marker of Object.values(this.markers)) {
      bounds.extend(marker.getPosition());
    }
    this.gmap.fitBounds(bounds);
  }

  /**
   * get the services of a request
   */
  loadServices(filters: ServicesFilters) {
    this.servicesDataList = null;
    this.flags.execute(MassiveServiceFlags.LOAD_SERVICES_TABLE, async test => {
      if (test()) {
        this.servicesDataList = await this.massiveService.listMassiveDeliveryService(this.requestId, filters);
      }
    });
  }

  /**
   * Remove markers from map
   */
  muOnDestroy() {
    this.layoutPageAndMapService.reset();
    if (this.mapCluster) {
      for (const marker of Object.values(this.markers)) {
        this.mapCluster.removeMarker(marker);
        marker.setMap(null);
        if (typeof google?.maps?.event?.clearListeners === 'function') {
          google.maps.event.clearListeners(marker, 'click');
        }
      }
      this.mapCluster.clearMarkers();
    }
    this.mapCluster = null;
    this.markers = {};
  }

  /**
   * Show marker
   *
   * @param serviceId
   */
  selectMapMarker(serviceId: string) {
    if (this.selectedMarker) {
      this.selectedMarker.setZIndex(massiveConstants.normalZIndex);
      this.selectedMarker.setIcon(massiveConstants.normalIcon);
    }
    const marker = this.markers[serviceId];
    this.gmap.setZoom(massiveConstants.selectZoom);
    this.gmap.setCenter(new google.maps.LatLngBounds(marker.getPosition()).getCenter());
    marker.setZIndex(massiveConstants.selectedZIndex);
    marker.setIcon(massiveConstants.selectedIcon);
    this.selectedMarker = marker;
  }
}
